import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  BellOff,
  Smartphone,
  Send,
  Trash2,
  Info,
  AlertTriangle,
  Apple,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import SectionToggle from "./SectionToggle";
import api from "../lib/api";
import {
  pushSupported,
  isIOS,
  isPWA,
  getPermission,
  subscribeThisDevice,
  unsubscribeThisDevice,
  currentSubscription,
  listDevices,
  removeDevice,
  sendTest,
  fetchServerStatus,
} from "../lib/webPush";
import { toast } from "sonner";
import { cn } from "../lib/utils";

/** Compute a friendly status string for THIS device. */
function useDeviceStatus(refreshKey) {
  const [state, setState] = useState({
    supported: false,
    configured: false,
    permission: "default",
    subscribed: false,
    iosNeedsPWA: false,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ---- Cheap state first: render the UI within one frame ----
      const supported = pushSupported();
      const iosNeedsPWA = isIOS() && !isPWA();
      const permission = await getPermission();
      let configured = false;
      try {
        const status = await fetchServerStatus();
        configured = !!status.configured;
      } catch {
        configured = false;
      }
      if (cancelled) return;
      setState({
        supported,
        iosNeedsPWA,
        permission,
        configured,
        subscribed: false,
        loading: false,
      });
      // ---- Expensive: subscription check via service worker ----
      if (supported) {
        try {
          const sub = await currentSubscription();
          if (!cancelled) {
            setState((s) => ({ ...s, subscribed: !!sub }));
          }
        } catch {
          // ignore
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);
  return state;
}

export default function PushNotificationSection({ settings, setSetting }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [devices, setDevices] = useState([]);
  const [working, setWorking] = useState(false);
  const status = useDeviceStatus(refreshKey);

  const loadDevices = async () => {
    try {
      const list = await listDevices();
      setDevices(list);
    } catch {
      setDevices([]);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [refreshKey]);

  const enable = async () => {
    setWorking(true);
    try {
      await subscribeThisDevice();
      toast.success("Push enabled on this device");
      setRefreshKey((n) => n + 1);
      // Also flip the global setting on so the scheduler delivers
      if (settings && settings.push_enabled === false) {
        await setSetting("push_enabled", true);
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't enable push");
    } finally {
      setWorking(false);
    }
  };

  const disable = async () => {
    setWorking(true);
    try {
      await unsubscribeThisDevice();
      toast.message("Push disabled on this device");
      setRefreshKey((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Couldn't disable push");
    } finally {
      setWorking(false);
    }
  };

  const onSendTest = async () => {
    setWorking(true);
    try {
      const { delivered } = await sendTest();
      if (delivered) {
        toast.success(`Sent test to ${delivered} device${delivered === 1 ? "" : "s"}`);
      } else {
        toast.message(
          "No subscribed devices yet — enable push on at least one device first."
        );
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't send test");
    } finally {
      setWorking(false);
    }
  };

  const onRemoveDevice = async (id) => {
    if (!window.confirm("Remove this device from push notifications?")) return;
    try {
      await removeDevice(id);
      toast.success("Device removed");
      setRefreshKey((n) => n + 1);
    } catch {
      toast.error("Couldn't remove device");
    }
  };

  // Status badge for THIS device
  const statusBadge = useMemo(() => {
    if (status.loading) return { label: "Checking…", color: "muted" };
    if (!status.supported) return { label: "Not supported", color: "muted" };
    if (status.iosNeedsPWA) return { label: "Install required", color: "warning" };
    if (!status.configured) return { label: "Server not configured", color: "muted" };
    if (status.permission === "denied") return { label: "Blocked", color: "danger" };
    if (status.subscribed && status.permission === "granted") return { label: "Enabled", color: "success" };
    return { label: "Disabled", color: "muted" };
  }, [status]);

  const colorClasses = {
    success: "border-green-500/40 text-green-500 bg-green-500/10",
    warning: "border-amber-500/40 text-amber-500 bg-amber-500/10",
    danger: "border-red-500/40 text-red-500 bg-red-500/10",
    muted: "border-border text-muted-foreground bg-secondary/40",
  };

  return (
    <section
      data-testid="settings-push-section"
      className="rounded-xl border border-border bg-card p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Bell size={14} className="text-primary" /> Push notifications
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Reminders that reach you across desktop, Android, and iPhone (Home-Screen install on iOS).
          </p>
        </div>
        <span
          data-testid="push-status-badge"
          className={cn(
            "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border",
            colorClasses[statusBadge.color]
          )}
        >
          {status.subscribed && status.permission === "granted" ? (
            <CheckCircle2 size={11} />
          ) : status.permission === "denied" ? (
            <BellOff size={11} />
          ) : (
            <Bell size={11} />
          )}
          {statusBadge.label}
        </span>
      </div>

      {/* Platform-aware explainer */}
      <PlatformExplainer status={status} />

      <div className="space-y-2.5 mt-4">
        {/* Master toggle (server-side) */}
        <ToggleCard
          icon={<Bell size={13} strokeWidth={2} />}
          label="Enable push notifications"
          description="Receive reminders even when this tab is closed."
          checked={settings?.push_enabled !== false}
          onToggle={() => setSetting("push_enabled", settings?.push_enabled === false)}
          testId="push-enabled-toggle"
        />
        <ToggleCard
          icon={<Clock size={13} strokeWidth={2} />}
          label="24-hour reminder"
          description="Push the day before a task is due"
          checked={settings?.push_reminders_24h !== false}
          onToggle={() => setSetting("push_reminders_24h", settings?.push_reminders_24h === false)}
          testId="push-24h-toggle"
          disabled={settings?.push_enabled === false}
        />
        <ToggleCard
          icon={<Clock size={13} strokeWidth={2} />}
          label="1-hour reminder"
          description="Push one hour before a task is due"
          checked={settings?.push_reminders_1h !== false}
          onToggle={() => setSetting("push_reminders_1h", settings?.push_reminders_1h === false)}
          testId="push-1h-toggle"
          disabled={settings?.push_enabled === false}
        />
        <div
          className={cn(
            "rounded-lg border px-3 py-3 transition-colors",
            settings?.push_daily_agenda !== false
              ? "border-primary/50 bg-primary/[0.04]"
              : "border-border bg-secondary/20"
          )}
        >
          <SectionToggle
            icon={<Send size={13} strokeWidth={2} />}
            label="Daily agenda"
            checked={settings?.push_daily_agenda !== false}
            onToggle={() =>
              setSetting("push_daily_agenda", settings?.push_daily_agenda === false)
            }
            accent="hsl(var(--primary))"
            testId="push-daily-agenda-toggle"
            ariaLabel="Daily agenda push"
          />
          <p className="text-[11px] text-muted-foreground mt-1.5 ml-9 leading-relaxed">
            One-tap summary of today's tasks at your chosen time.
          </p>
          {settings?.push_daily_agenda !== false && (
            <div className="mt-2 ml-9 flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                Time
              </Label>
              <Input
                data-testid="push-daily-agenda-time"
                type="time"
                className="w-32"
                value={settings?.push_daily_agenda_time || "07:00"}
                onChange={(e) =>
                  setSetting("push_daily_agenda_time", e.target.value || "07:00")
                }
              />
              <span className="text-[10px] text-muted-foreground truncate">
                {settings?.push_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Per-device controls */}
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        {status.subscribed ? (
          <Button
            variant="outline"
            onClick={disable}
            disabled={working}
            data-testid="push-disable-this-device-btn"
          >
            <BellOff size={13} className="mr-1.5" /> Disable on this device
          </Button>
        ) : (
          <Button
            onClick={enable}
            disabled={working || !status.supported || status.iosNeedsPWA || !status.configured}
            data-testid="push-enable-this-device-btn"
          >
            <Bell size={13} className="mr-1.5" /> Enable on this device
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onSendTest}
          disabled={working || !status.configured}
          data-testid="push-send-test-btn"
        >
          <Send size={13} className="mr-1.5" /> Send test
        </Button>
      </div>

      {/* Device list */}
      {devices.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            Your devices
          </div>
          <ul data-testid="push-device-list" className="space-y-2">
            {devices.map((d) => (
              <li
                key={d.id}
                className="flex items-center gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2"
                data-testid={`push-device-${d.id}`}
              >
                <Smartphone size={14} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {d.device_label || "Device"}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {(d.user_agent || "").slice(0, 80)}
                  </div>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                  onClick={() => onRemoveDevice(d.id)}
                  aria-label="Remove device"
                  data-testid={`push-device-remove-${d.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ToggleCard({ icon, label, description, checked, onToggle, testId, disabled }) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-3 transition-colors",
        disabled && "opacity-50 pointer-events-none",
        checked
          ? "border-primary/50 bg-primary/[0.04]"
          : "border-border bg-secondary/20"
      )}
    >
      <SectionToggle
        icon={icon}
        label={label}
        checked={checked}
        onToggle={onToggle}
        accent="hsl(var(--primary))"
        testId={testId}
        ariaLabel={label}
      />
      {description && (
        <p className="text-[11px] text-muted-foreground mt-1.5 ml-9 leading-relaxed">
          {description}
        </p>
      )}
    </div>
  );
}

function PlatformExplainer({ status }) {
  if (status.loading) return null;
  // Three flavours: iOS needs install, blocked, generic capability info.
  if (status.iosNeedsPWA) {
    return (
      <div
        data-testid="push-explainer-ios"
        className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs flex items-start gap-2"
      >
        <Apple size={14} className="text-amber-500 mt-0.5 shrink-0" />
        <div>
          <div className="font-bold text-amber-500 mb-1">
            Install FlowTasks360 to your Home Screen
          </div>
          <p className="text-muted-foreground leading-relaxed">
            iOS only delivers Web Push to PWAs. In Safari, tap{" "}
            <span className="font-medium">Share ▸ Add to Home Screen</span>, then open
            FlowTasks from your Home Screen and come back to enable push.
          </p>
        </div>
      </div>
    );
  }
  if (status.permission === "denied") {
    return (
      <div
        data-testid="push-explainer-blocked"
        className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-xs flex items-start gap-2"
      >
        <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
        <div>
          <div className="font-bold text-red-500 mb-1">Notifications are blocked</div>
          <p className="text-muted-foreground leading-relaxed">
            Re-enable them in your browser settings (in Chrome: 🔒 lock icon ▸ Site
            settings ▸ Notifications), then refresh.
          </p>
        </div>
      </div>
    );
  }
  if (!status.supported) {
    return (
      <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs flex items-start gap-2">
        <Info size={14} className="text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-muted-foreground leading-relaxed">
          This browser doesn't support Web Push. Open FlowTasks in Chrome, Edge, Safari (iOS
          16.4+ Home-Screen install), or another modern browser to enable.
        </p>
      </div>
    );
  }
  return (
    <details
      data-testid="push-explainer-capabilities"
      className="rounded-lg border border-border bg-secondary/30 p-3 text-xs"
    >
      <summary className="cursor-pointer font-medium text-muted-foreground flex items-center gap-2">
        <Info size={12} /> Platform capability notes
      </summary>
      <ul className="mt-2 space-y-1.5 text-muted-foreground list-disc pl-4 leading-relaxed">
        <li>
          <strong className="text-foreground">iOS 16.4+</strong>: Web Push works
          <em> only </em> after the user installs FlowTasks to the Home Screen.
        </li>
        <li>
          <strong className="text-foreground">Android &amp; desktop</strong>: Push works in
          Chrome, Edge, Firefox; usually wakes the browser in the background.
        </li>
        <li>
          <strong className="text-foreground">Background geofencing</strong> isn't
          available in any PWA — it requires a native iOS/Android wrapper (on our
          roadmap). Foreground geofence reminders work today while the app is open.
        </li>
        <li>
          The reminders you opt into here run in <strong className="text-foreground">parallel</strong>
          {" "}with email reminders — disable either independently.
        </li>
      </ul>
    </details>
  );
}
